import { servicesUrlConfig } from "shared/config/setting";

/**
 * Users API Service
 * Centralized API calls for user management
 */
export const usersService = {
  /**
   * Get all users with pagination
   * @param {function} authFetch - Authenticated fetch function
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Items per page (default: 10)
   * @returns {Promise} API response with users data
   */
  async getAll(authFetch, page = 1, perPage = 10) {
    const url = `${servicesUrlConfig.USERS_API.get_all}?page=${page}&per_page=${perPage}`;
    return await authFetch(url);
  },

  /**
   * Get all users with their organization roles
   * @param {function} authFetch - Authenticated fetch function
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Items per page (default: 10)
   * @returns {Promise} API response with users and their roles
   */
  async getUsersOrgsRoles(authFetch, page = 1, perPage = 10) {
    const url = `${servicesUrlConfig.USERS_API.orgs_roles}?page=${page}&per_page=${perPage}`;
    return await authFetch(url);
  },

  /**
   * Get user by ID
   * @param {string} userId - User ID
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response with user details
   */
  async getById(userId, authFetch) {
    const url = `${servicesUrlConfig.USERS_API.get_by_id}${userId}`;
    return await authFetch(url);
  },

  /**
   * Get current user information
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response with current user data
   */
  async getCurrentUser(authFetch) {
    const url = servicesUrlConfig.USERS_API.me;
    return await authFetch(url);
  },

  /**
   * Set user password (admin only)
   * @param {string} username - Username
   * @param {string} password - New password
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response
   */
  async setUserPassword(username, password, authFetch) {
    const url = `${servicesUrlConfig.AUTH_API.admin_set_user_password}/${username}`;
    return await authFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ password }),
    });
  },

  /**
   * Update a user (admin only)
   * @param {string} username - Username
   * @param {object} data - Partial user fields, e.g. { is_superuser: true }
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response with updated user
   */
  async updateUser(username, data, authFetch) {
    const url = `${servicesUrlConfig.USERS_API.update}${username}`;
    return await authFetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  },
};
